import AssignmentCard, {
  type AssignmentCardProps,
} from "@/components/design-system/assignment-card";
import ClassCard, {
  formatClassDashboardDate,
  type ClassCardItem,
} from "@/components/design-system/class-card";
import { AppDispatch } from "@/store";
import {
  fetchStudentClassAssignments,
  selectStudentClassAssignments,
  selectStudentClassAssignmentsError,
  selectStudentClassAssignmentsLoading,
} from "@/store/slices/studentClassAssignmentsSlice";
import {
  fetchStudentClassDashboardData,
  selectStudentClassDashboardError,
  selectStudentClassDashboardLoading,
} from "@/store/slices/studentClassDashboardSlice";
import type { StudentAssignment } from "@/types/assignment.model";
import { StudentClassDashboardItem } from "@/types/student-class-dashboard.model";
import { normalizeClassThumbnailUrl } from "@/utils/classThumbnail.utils";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { buildCsViewCoursePath } from "@/modules/Course-Management/utils/csViewContext";
import { RootState } from "@/store";

type AssignmentStatus = AssignmentCardProps["status"];

type ClassDetailNavigationState = {
  viewType: "class";
  courseGradeId?: string;
  selectedTab?: string;
  selectedCardId?: string;
  assignmentType?: string;
  lessonId?: string | null;
  unitId?: string | null;
};

function getAssignmentStatus(statusLabel: string): AssignmentStatus {
  const normalized = statusLabel.toUpperCase();

  if (normalized === "COMPLETED") return "Completed";
  if (normalized === "REVIEW" || normalized.includes("FEEDBACK")) {
    return "New Feedback";
  }
  return "In Progress";
}

function getClassResourceId(assignment: StudentAssignment): string | undefined {
  return assignment.grade?.id;
}

const CLASS_LIST_PREVIEW_COUNT = 2;

export default function StudentClassDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [showAllClasses, setShowAllClasses] = useState(false);

  const dashboardData = useSelector(
    (state: RootState) => state.studentClassDashboard.data
  );
  const loading = useSelector(selectStudentClassDashboardLoading);
  const error = useSelector(selectStudentClassDashboardError);

  const assignments = useSelector(selectStudentClassAssignments);
  const assignmentsLoading = useSelector(selectStudentClassAssignmentsLoading);
  const assignmentsError = useSelector(selectStudentClassAssignmentsError);

  useEffect(() => {
    dispatch(fetchStudentClassDashboardData());
    dispatch(fetchStudentClassAssignments({ page: 1, pageSize: 10 }));
  }, [dispatch]);

  const unitAssignments = useMemo(
    () => assignments.filter((assignment) => assignment.assign_type === "UNIT"),
    [assignments]
  );

  const generalAssignments = useMemo(
    () =>
      assignments.filter((assignment) => assignment.assign_type === "GENERAL"),
    [assignments]
  );

  const allClasses = dashboardData?.classes ?? [];
  const hasMoreClasses = allClasses.length > CLASS_LIST_PREVIEW_COUNT;
  const visibleClasses = showAllClasses
    ? allClasses
    : allClasses.slice(0, CLASS_LIST_PREVIEW_COUNT);

  const navigateToClassDetail = useCallback(
    (classId: string, state?: Omit<ClassDetailNavigationState, "viewType">) => {
      navigate(buildCsViewCoursePath(classId, "class"), {
        state: { viewType: "class", ...state },
      });
    },
    [navigate]
  );

  const navigateToAssignment = useCallback(
    (assignment: StudentAssignment) => {
      const classId = getClassResourceId(assignment);
      if (!classId) return;

      navigateToClassDetail(classId, {
        selectedTab: "assignment",
        selectedCardId: assignment.id,
        assignmentType: assignment.assign_type === "UNIT" ? "unit" : "general",
        lessonId: assignment.lesson?.id ?? null,
        unitId: assignment.unit?.id ?? null,
      });
    },
    [navigateToClassDetail]
  );

  const mapClassToCard = useCallback(
    (classItem: StudentClassDashboardItem): ClassCardItem => ({
      title: classItem.name,
      status: classItem.status,
      imageUrl:
        normalizeClassThumbnailUrl(classItem.thumbnail_image_url) ||
        "/images/course_thumbnail.png",
      totalLessons: parseInt(classItem.lessons || "0", 10) || 0,
      totalUnits: parseInt(classItem.units || "0", 10) || 0,
      lessonsCompleted: classItem.dash?.lessons ?? 0,
      unitsCompleted: classItem.dash?.units ?? 0,
      completionPercent:
        parseInt((classItem.dash?.completion || "0%").replace("%", ""), 10) ||
        0,
      purchasedOn: formatClassDashboardDate(classItem.class_purchased_on),
      expiryOn: formatClassDashboardDate(classItem.class_expiring_on),
      onViewClass: () => navigateToClassDetail(classItem.id),
      viewClassDisabled: false,
    }),
    [navigateToClassDetail]
  );

  const renderClassContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="animate-spin" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-red-500 py-4">
          Error loading class data: {error}
        </div>
      );
    }

    if (!dashboardData?.classes?.length) {
      return <div className="text-gray-500 py-4">No class data available</div>;
    }

    return (
      <div className="space-y-4">
        {visibleClasses.map((classItem, index) => (
          <div key={classItem.id || index} className="mb-4">
            <ClassCard classItem={mapClassToCard(classItem)} />
          </div>
        ))}
      </div>
    );
  };

  const renderUnitAssignments = () => {
    if (assignmentsLoading) {
      return (
        <div className="flex justify-center items-center w-full py-4">
          <Loader2 className="animate-spin" />
        </div>
      );
    }

    if (assignmentsError) {
      return (
        <div className="flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]">
          <p className="text-red-500 text-[16px] font-bold mb-[9px]">
            Error loading unit assignments: {assignmentsError}
          </p>
        </div>
      );
    }

    if (unitAssignments.length === 0) {
      return (
        <div className="flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]">
          <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
            No Unit Assignments assigned yet.
          </p>
          <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
            Your teacher-assigned tasks will appear here.
          </p>
        </div>
      );
    }

    return unitAssignments.map((assignment) => (
      <AssignmentCard
        key={assignment.id}
        courseTitle={assignment.name}
        status={getAssignmentStatus(assignment.status.label)}
        unitName={assignment.unit?.name}
        lessonName={assignment.lesson?.name}
        dateTime={assignment.created_date}
        onViewAssignment={() => navigateToAssignment(assignment)}
      />
    ));
  };

  const renderGeneralAssignments = () => {
    if (assignmentsLoading) {
      return (
        <div className="flex justify-center items-center w-full py-4">
          <Loader2 className="animate-spin" />
        </div>
      );
    }

    if (assignmentsError) {
      return (
        <div className="flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]">
          <p className="text-red-500 text-[16px] font-bold mb-[9px]">
            Error loading general assignments: {assignmentsError}
          </p>
        </div>
      );
    }

    if (generalAssignments.length === 0) {
      return (
        <div className="flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]">
          <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
            No General Assignments assigned yet.
          </p>
          <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
            Your teacher-assigned tasks will appear here.
          </p>
        </div>
      );
    }

    return generalAssignments.map((assignment) => (
      <AssignmentCard
        key={assignment.id}
        courseTitle={assignment.name}
        status={getAssignmentStatus(assignment.status.label)}
        dateTime={assignment.created_date}
        onViewAssignment={() => navigateToAssignment(assignment)}
      />
    ));
  };

  const firstClassId = allClasses[0]?.id;

  const handleViewAllAssignments = () => {
    if (!firstClassId) return;
    navigateToClassDetail(firstClassId, { selectedTab: "assignment" });
  };

  return (
    <div className="bg-white rounded-[12px] p-[24px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[20px] text-[#1F1F1F] font-bold">
          {allClasses.length > 1
            ? `Classes(${String(allClasses.length).padStart(2, "0")})`
            : "Class"}
        </h2>
        {hasMoreClasses && !showAllClasses ? (
          <button
            type="button"
            onClick={() => setShowAllClasses(true)}
            className="text-[#5C88C5] text-[16px] font-[600] cursor-pointer outline-none border-none"
          >
            View all
          </button>
        ) : null}
      </div>
      {renderClassContent()}

      <div className="mb-[24px] mt-[24px]">
        <div className="flex justify-between items-center mb-[19px]">
          <h2 className="text-[20px] text-[#1F1F1F] font-bold">
            Unit Assignments ({String(unitAssignments.length).padStart(2, "0")})
          </h2>
          {unitAssignments.length > 0 ? (
            <button
              type="button"
              onClick={handleViewAllAssignments}
              className="text-[#5C88C5] text-[16px] font-[600] cursor-pointer outline-none border-none"
            >
              View all
            </button>
          ) : null}
        </div>
        <div className="flex justify-start gap-[17px] flex-wrap">
          {renderUnitAssignments()}
        </div>
      </div>

      <div className="mb-[24px]">
        <h2 className="text-[20px] text-[#1F1F1F] font-bold mb-[19px]">
          General Assignments (
          {String(generalAssignments.length).padStart(2, "0")})
        </h2>
        <div className="flex justify-start gap-[16px] flex-wrap">
          {renderGeneralAssignments()}
        </div>
      </div>
    </div>
  );
}
